import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Eye } from 'lucide-react'
import { useForm } from '../hooks/useForm'
import { useToast } from '../components/Toast'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import TextArea from '../components/ui/Textarea'
import Modal from '../components/Modal'
import CupomTermicoPreview from '../components/CupomTermicoPreview'
import cuponsTermicosService, {
  CupomTermico,
  CupomTermicoFormData,
  tiposCupomTermico,
  configuracoesTermicas,
  configuracaoPadrao
} from '../services/templates'
import settingsService from '../services/settings'
import { useRequireAuth } from '../context/AuthContext'

const CupomTermicoFormPage: React.FC = () => {
  useRequireAuth(['ADMIN'])

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()

  const [cupom, setCupom] = useState<CupomTermico | null>(null)
  const [loading, setLoading] = useState(!!id)
  const [showPreview, setShowPreview] = useState(false)
  const [dadosEmpresa, setDadosEmpresa] = useState({
    nome: 'NexaMakers OS',
    nif: '',
    endereco: '',
    telefone: '',
    email: '',
    website: ''
  })

  const isEditing = !!id

  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue
  } = useForm({
    initialValues: {
      nome: '',
      descricao: '',
      tipo: 'ORDEM_SERVICO_ABERTURA' as const,
      configuracao: configuracaoPadrao,
      ativo: true,
      padrao: false
    } as CupomTermicoFormData,
    validationRules: {
      nome: {
        required: 'Nome é obrigatório',
        minLength: { value: 3, message: 'Nome deve ter pelo menos 3 caracteres' }
      },
      tipo: {
        required: 'Tipo é obrigatório'
      }
    },
    onSubmit: async (formData) => {
      try {
        if (isEditing && cupom) {
          await cuponsTermicosService.update(cupom.id, formData)
          success('Cupom térmico atualizado com sucesso')
        } else {
          await cuponsTermicosService.create(formData)
          success('Cupom térmico criado com sucesso')
        }
        navigate('/cupons-termicos')
      } catch (err: any) {
        showError(
          isEditing ? 'Erro ao atualizar cupom térmico' : 'Erro ao criar cupom térmico',
          err.message
        )
      }
    }
  })

  useEffect(() => {
    loadDadosEmpresa()
    if (id) {
      loadCupom()
    }
  }, [id])

  const loadDadosEmpresa = async () => {
    try {
      const dados = await settingsService.getEmpresaForTemplates()
      setDadosEmpresa({
        nome: dados.nome || 'NexaMakers OS',
        nif: dados.nif || '',
        endereco: `${dados.morada || ''} ${dados.codigoPostal || ''} ${dados.cidade || ''}`.trim(),
        telefone: dados.telefone || '',
        email: dados.email || '',
        website: dados.website || ''
      })
    } catch (err: any) {
      console.error('Erro ao carregar dados da empresa:', err)
      // Manter valores padrão se houver erro
    }
  }

  const loadCupom = async () => {
    try {
      setLoading(true)
      const cupomData = await cuponsTermicosService.getById(parseInt(id!))
      setCupom(cupomData)

      // Preencher o formulário
      setFieldValue('nome', cupomData.nome)
      setFieldValue('descricao', cupomData.descricao || '')
      setFieldValue('tipo', cupomData.tipo)
      setFieldValue('configuracao', cupomData.configuracao)
      setFieldValue('ativo', cupomData.ativo)
      setFieldValue('padrao', cupomData.padrao)
    } catch (err: any) {
      showError('Erro ao carregar cupom térmico', err.message)
      navigate('/cupons-termicos')
    } finally {
      setLoading(false)
    }
  }

  const updateConfiguracao = (campo: keyof typeof configuracaoPadrao, valor: any) => {
    setFieldValue('configuracao', {
      ...values.configuracao,
      [campo]: valor
    })
  }

  const dadosPreview = {
    id: 1,
    tipo: values.tipo,
    dadosEmpresa: {
      nome: dadosEmpresa.nome,
      nif: dadosEmpresa.nif,
      endereco: dadosEmpresa.endereco,
      telefone: dadosEmpresa.telefone,
      email: dadosEmpresa.email,
      website: dadosEmpresa.website
    },
    dadosCliente: {
      nome: 'Cliente Exemplo',
      telefone: '(11) 9876-5432',
      email: 'cliente@email.com',
      documento: '123.456.789-00'
    },
    dadosEquipamento: {
      tipo: 'Smartphone',
      marca: 'Samsung',
      modelo: 'Galaxy S21',
      numeroSerie: 'SN123456789',
      imei: '353456789012345',
      cor: 'Preto',
      estadoFisico: 'BOM',
      observacoes: 'Riscos leves na traseira',
      acessorios: 'Carregador, cabo USB',
      senhaDesbloqueio: '1234'
    },
    itens: values.tipo === 'ORCAMENTO' || values.tipo === 'ORDEM_SERVICO_FECHAMENTO' ? [
      {
        descricao: 'Troca de tela',
        quantidade: 1,
        valorUnitario: 150.00,
        valorTotal: 150.00
      },
      {
        descricao: 'Mão de obra',
        valorTotal: 50.00
      }
    ] : undefined,
    valorTotal: values.tipo === 'ORCAMENTO' || values.tipo === 'ORDEM_SERVICO_FECHAMENTO' ? 200.00 : undefined,
    observacoes: values.tipo === 'ORDEM_SERVICO_ABERTURA' ? 'Equipamento com tela quebrada' : 'Garantia de 90 dias',
    dataHora: new Date().toLocaleString('pt-PT'),
    numeroDocumento: values.tipo === 'ORDEM_SERVICO_ABERTURA' || values.tipo === 'ORDEM_SERVICO_FECHAMENTO' ? 'OS-2024-001' :
                      values.tipo === 'ORCAMENTO' ? 'ORC-2024-001' : 'REC-2024-001'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/cupons-termicos')}
          leftIcon={<ArrowLeft className="h-4 w-4" />}
        >
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Editar Cupom Térmico' : 'Novo Cupom Térmico'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Altere as configurações do cupom térmico' : 'Configure um novo modelo de cupom térmico'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Informações Básicas */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Informações Básicas</h2>

              <div className="space-y-4">
                <Input
                  label="Nome do Cupom *"
                  placeholder="Ex: Recebimento Padrão"
                  {...getFieldProps('nome')}
                />

                <TextArea
                  label="Descrição"
                  placeholder="Descreva quando este cupom deve ser usado"
                  rows={3}
                  {...getFieldProps('descricao')}
                />

                <Select
                  label="Tipo de Cupom *"
                  {...getFieldProps('tipo')}
                  options={[
                    { value: '', label: 'Selecione o tipo' },
                    ...tiposCupomTermico.map(tipo => ({
                      value: tipo.value,
                      label: tipo.label
                    }))
                  ]}
                />

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ativo"
                      checked={values.ativo}
                      onChange={(e) => setFieldValue('ativo', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="ativo" className="ml-2 block text-sm text-gray-900">
                      Cupom ativo
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="padrao"
                      checked={values.padrao}
                      onChange={(e) => setFieldValue('padrao', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="padrao" className="ml-2 block text-sm text-gray-900">
                      Usar como padrão
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Configurações de Impressão */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Configurações de Impressão</h2>

              <div className="space-y-4">
                <Select
                  label="Largura do Papel"
                  value={values.configuracao.larguraPapel.toString()}
                  onChange={(e) => updateConfiguracao('larguraPapel', parseInt(e.target.value) as 80 | 58)}
                  options={[
                    { value: '', label: 'Selecione a largura' },
                    ...configuracoesTermicas.largurasPapel.map(largura => ({
                      value: largura.value.toString(),
                      label: largura.label
                    }))
                  ]}
                />

                <Select
                  label="Tamanho da Fonte"
                  value={values.configuracao.tamanhoFonte}
                  onChange={(e) => updateConfiguracao('tamanhoFonte', e.target.value)}
                  options={[
                    { value: '', label: 'Selecione o tamanho' },
                    ...configuracoesTermicas.tamanhosFonte.map(tamanho => ({
                      value: tamanho.value,
                      label: tamanho.label
                    }))
                  ]}
                />

                <Input
                  label="Número de Vias"
                  type="number"
                  min="1"
                  max="3"
                  value={values.configuracao.numeroVias}
                  onChange={(e) => updateConfiguracao('numeroVias', parseInt(e.target.value) || 1)}
                />

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="cortarPapel"
                      checked={values.configuracao.cortarPapel}
                      onChange={(e) => updateConfiguracao('cortarPapel', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="cortarPapel" className="ml-2 block text-sm text-gray-900">
                      Cortar papel automaticamente
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="abrirGaveta"
                      checked={values.configuracao.abrirGaveta}
                      onChange={(e) => updateConfiguracao('abrirGaveta', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="abrirGaveta" className="ml-2 block text-sm text-gray-900">
                      Abrir gaveta após impressão
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="incluirLogo"
                      checked={values.configuracao.incluirLogo}
                      onChange={(e) => updateConfiguracao('incluirLogo', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="incluirLogo" className="ml-2 block text-sm text-gray-900">
                      Incluir logo da empresa
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Preview do Cupom</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                  leftIcon={<Eye className="h-4 w-4" />}
                >
                  Visualizar Completo
                </Button>
              </div>

              <div className="p-4">
                <div
                  className={`
                    bg-gray-50 border border-gray-300 rounded-lg p-4 font-mono text-xs
                    overflow-auto max-h-96 whitespace-pre-line
                    ${values.configuracao.larguraPapel === 58 ? 'max-w-sm' : 'max-w-lg'}
                  `}
                  style={{
                    fontFamily: '"Courier New", Courier, monospace',
                    fontSize: values.configuracao.tamanhoFonte === 'pequena' ? '10px' :
                              values.configuracao.tamanhoFonte === 'grande' ? '14px' : '12px'
                  }}
                >
                  {/* Preview simples - mostrar apenas as primeiras linhas */}
                  {values.tipo === 'ORDEM_SERVICO_ABERTURA' && `
                ${dadosEmpresa.nome}
           ORDEM DE SERVICO - ABERTURA
===============================================
OS Nº:                           OS-2024-001
Data:                    ${new Date().toLocaleDateString('pt-PT')}
-----------------------------------------------
DADOS DO CLIENTE
-----------------------------------------------
Nome: Cliente Exemplo
Tel: (11) 9876-5432
Email: cliente@email.com
-----------------------------------------------
                  `}
                  {values.tipo === 'ORCAMENTO' && `
                ${dadosEmpresa.nome}
                  ORCAMENTO
===============================================
Orcamento Nº:                    ORC-2024-001
Data:                    ${new Date().toLocaleDateString('pt-PT')}
-----------------------------------------------
Cliente: Cliente Exemplo
Telefone: (11) 9876-5432
-----------------------------------------------
ITENS/SERVICOS:
-----------------------------------------------
1. Troca de tela
   1x R$ 150.00                    R$ 150.00

2. Mão de obra                      R$ 50.00

===============================================
TOTAL GERAL:                       R$ 200.00
===============================================
                  `}
                  {values.tipo === 'ORDEM_SERVICO_FECHAMENTO' && `
                ${dadosEmpresa.nome}
         ORDEM DE SERVICO - ENTREGA
===============================================
OS Nº:                           OS-2024-001
Data:                    ${new Date().toLocaleDateString('pt-PT')}
-----------------------------------------------
Cliente: Cliente Exemplo
-----------------------------------------------
SERVICOS REALIZADOS:
-----------------------------------------------
1. Troca de tela                   R$ 150.00

2. Mão de obra                      R$ 50.00

===============================================
TOTAL:                             R$ 200.00
===============================================
                  `}
                  {values.tipo === 'RECIBO' && `
                ${dadosEmpresa.nome}
                    RECIBO
===============================================
Recibo Nº:                       REC-2024-001
Data:                    ${new Date().toLocaleDateString('pt-PT')}
-----------------------------------------------
Recebi de: Cliente Exemplo
-----------------------------------------------
Valor: R$ 200.00
Referente a: Servicos prestados
===============================================
                  `}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/cupons-termicos')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {isEditing ? 'Atualizar Cupom' : 'Criar Cupom'}
          </Button>
        </div>
      </form>

      {/* Modal de preview completo */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Preview Completo do Cupom"
        size="lg"
      >
        <CupomTermicoPreview
          cupom={{
            id: 0,
            nome: values.nome || 'Cupom em criação',
            tipo: values.tipo,
            configuracao: values.configuracao,
            ativo: values.ativo,
            padrao: values.padrao,
            versao: '1.0',
            criadoPor: 1,
            ultimaModificacao: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            criador: {
              id: 1,
              nome: 'Usuário Atual',
              email: 'usuario@nexamakers.com'
            }
          }}
          dados={dadosPreview}
          onImprimir={() => {
            success('Preview enviado para impressão')
            setShowPreview(false)
          }}
        />
      </Modal>
    </div>
  )
}

export default CupomTermicoFormPage